import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IFactoryResponse } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'

const resource = 'integrations'

export type IntegrationMode = 'live' | 'readonly' | 'off'
export type IntegrationJobStatus = 'pending' | 'running' | 'success' | 'failed' | 'skipped'
export type IntegrationErrorCode = 'AUTH' | 'NOT_FOUND' | 'RATE_LIMIT' | 'SERVER' | 'NETWORK' | 'VALIDATION' | 'CONFIG' | 'DATA' | 'UNKNOWN'

export type IntegrationHealth = {
  status: 'healthy' | 'warning' | 'error'
  windowDays: number
  counts: Partial<Record<IntegrationJobStatus, number>>
  openFailures: number
  pending: number
  retrying: number
  lastSuccessAt: string | null
  lastFailureAt: string | null
  lastErrorCode: IntegrationErrorCode | null
}

export type IntegrationSummary = {
  key: string
  label: string
  category: string
  capabilities: string[]
  configured: boolean
  generic?: boolean
  health: IntegrationHealth | null
}

export type JenjiProjectSyncSettings = {
  enabled: boolean
}

// An N2F company of the firm, in business words
export type N2fCompany = {
  label: string
  countryIds: string[]
  currency: string | null
  projects: boolean
  clients: boolean
  employees: boolean
}

export type IntegrationDetails = IntegrationSummary & {
  mode: IntegrationMode
  // Fields depend on the tool: Jenji (organization, rights, expenses), N2F (companies, clients)
  details?: {
    organizationId?: string | null
    authMethod?: string | null
    hasCredentials: boolean
    projectSync: JenjiProjectSyncSettings
    employeeSync: { enabled: boolean; create: boolean; update: boolean }
    rightsSync?: { enabled: boolean }
    clientSync?: { enabled: boolean }
    expensesImport?: { enabled: boolean; last: Pick<IntegrationJob, 'status' | 'result' | 'lastError' | 'finishedAt' | '_created'> | null }
    companies?: N2fCompany[]
    // the tool locked the access after too many calls: sends resume automatically after this date
    pausedUntil?: string | null
    features: {
      values: Partial<Record<IntegrationFeatureKey, boolean>>
      // 'legacy': taken from what the firm had with its former webhooks, 'explicit': chosen in settings
      sources: Partial<Record<IntegrationFeatureKey, 'legacy' | 'explicit'>>
      defaults: Partial<Record<IntegrationFeatureKey, boolean>>
    }
  }
}

export type IntegrationJobStep = {
  label?: string
  method?: string
  path?: string
  status?: number
  durationMs?: number
  ok?: boolean
  error?: string
  responseExcerpt?: string
  at: string
}

export type IntegrationJob = {
  _id: string
  provider: string
  type: string
  trigger: 'event' | 'manual' | 'retry' | 'nightly' | 'schedule'
  entityType?: string
  entityId?: string
  entityLabel?: string
  status: IntegrationJobStatus
  attempts: number
  maxAttempts: number
  nextRunAt?: string
  lastError?: { code: IntegrationErrorCode; message: string; status?: number; retryable?: boolean }
  skippedReason?: string
  result?: any
  payload?: any
  steps?: IntegrationJobStep[]
  children?: Pick<IntegrationJob, '_id' | 'type' | 'status' | 'entityLabel' | 'lastError' | '_created'>[]
  triggeredBy?: { _id: string; email: string }
  finishedAt?: string
  _created: string
}

export type IntegrationJobsQuery = {
  page?: number
  limit?: number
  status?: IntegrationJobStatus
  type?: string
  search?: string
  entityId?: string
}

export type IntegrationJobsResponse = {
  docs: IntegrationJob[]
  count: { total: number; currentPage: number; totalPages: number }
}

export type ProjectIssue = 'optionMissing' | 'optionLabel' | 'optionDisabled' | 'groupMissing' | 'groupCodeChanged' | 'groupLabel' | 'validityDiffers' | 'projectCodeMissing' | 'duplicateCode'

export type DiagnosticRow = {
  projectId: string
  projectCode: string
  display: string
  isArchived: boolean
  created: string
  issues: ProjectIssue[]
  warnings: { type: string; [key: string]: any }[]
}

export type DiagnosticReport = {
  checkedAt: string
  mode: IntegrationMode
  listId?: string
  summary: {
    projectsChecked: number
    projectsWithIssues: number
    byIssue: Partial<Record<ProjectIssue, number>>
    jenjiOptions?: number
    jenjiGroups?: number
    companies?: number
    orphanOptions: number
    orphanGroups: number
  }
  rows: DiagnosticRow[]
  orphans: {
    options: { id: string; label: string }[]
    groups: { id: string; internalId: string; name: string }[]
  }
}

export type IntegrationProjectOption = {
  _id: string
  display: string
  projectCode: string
  name: string
  isArchived: boolean
  client: { display: string } | null
}

export type ProjectCheckResult = {
  checkedAt: string
  project: { _id: string; projectCode: string; display: string; isArchived: boolean }
  // created with "Send to Jenji" unchecked
  excluded: boolean
  jenji?: {
    option: { label: string; disabled: boolean } | null
    group: { name: string } | null
  }
  // one line per N2F company receiving projects
  n2f?: {
    companies: {
      uuid: string
      label: string
      countryIds: string[]
      currency: string | null
      found: boolean
      name: string | null
      validityDateFrom: string | null
      validityDateTo: string | null
      nameDiffers: boolean
      validityDiffers: boolean
    }[]
  }
  issues: ProjectIssue[]
  warnings: { type: string; [key: string]: any }[]
  lastJob: IntegrationJob | null
}

export type ConnectionTestResult = {
  ok: boolean
  mode: IntegrationMode
  code?: IntegrationErrorCode | 'MODE_OFF'
  status?: number
  message?: string
  organizationName?: string | null
  groups?: number
  listId?: string
  options?: number
  companies?: number
  missingCompanies?: string[]
}

const baseUrl = `${BASE_URL}/${API_MAIN_VERSION}/${resource}`

export type IntegrationTouchpointEvent = 'employee.create' | 'project.create' | 'client.create'
export type IntegrationTouchpoint = { key: string; label: string; defaultChecked: boolean }

// Integrations offered as "send to…" checkboxes on a creation screen (any user of the firm)
const getTouchpoints = (axios: IFactoryFunctionRequest<any, any>) => (event: IntegrationTouchpointEvent, firmId?: string): IFactoryResponse<IntegrationTouchpoint[], any> => {
  return axios({ method: EMethod.get, url: `${baseUrl}/touchpoints`, query: { event, ...(firmId ? { firmId } : {}) } })
}

export type IntegrationFeatureKey = 'projectSync' | 'clientSync' | 'employeeCreate' | 'employeeUpdate' | 'rightsSync' | 'expensesImport'

// Settings > Integrations: what is synchronized for the firm
const updateSettings = (axios: IFactoryFunctionRequest<any, any>) => (provider: string, data: Partial<Record<IntegrationFeatureKey, boolean>>): IFactoryResponse<IntegrationDetails['details'], any> => {
  return axios({ method: EMethod.patch, url: `${baseUrl}/${provider}/settings`, data })
}

const listIntegrations = (axios: IFactoryFunctionRequest<any, any>) => (): IFactoryResponse<{ mode: IntegrationMode; providers: IntegrationSummary[] }, any> => {
  return axios({ method: EMethod.get, url: baseUrl })
}

const getIntegration = (axios: IFactoryFunctionRequest<any, any>) => (provider: string): IFactoryResponse<IntegrationDetails, any> => {
  return axios({ method: EMethod.get, url: `${baseUrl}/${provider}` })
}

const listJobs = (axios: IFactoryFunctionRequest<any, any>) => (provider: string, query: IntegrationJobsQuery = {}): IFactoryResponse<IntegrationJobsResponse, any> => {
  return axios({ method: EMethod.get, url: `${baseUrl}/${provider}/jobs`, query })
}

const getJob = (axios: IFactoryFunctionRequest<any, any>) => (provider: string, jobId: string, immediate = true): IFactoryResponse<IntegrationJob, any> => {
  return axios({ method: EMethod.get, url: `${baseUrl}/${provider}/jobs/${jobId}`, immediate })
}

const retryJob = (axios: IFactoryFunctionRequest<any, any>) => (provider: string, jobId: string): IFactoryResponse<IntegrationJob, any> => {
  return axios({ method: EMethod.post, url: `${baseUrl}/${provider}/jobs/${jobId}/retry` })
}

const dismissJob = (axios: IFactoryFunctionRequest<any, any>) => (provider: string, jobId: string): IFactoryResponse<IntegrationJob, any> => {
  return axios({ method: EMethod.post, url: `${baseUrl}/${provider}/jobs/${jobId}/dismiss` })
}

const retryFailedJobs = (axios: IFactoryFunctionRequest<any, any>) => (provider: string): IFactoryResponse<{ queued: number }, any> => {
  return axios({ method: EMethod.post, url: `${baseUrl}/${provider}/jobs/retry-failed` })
}

const testConnection = (axios: IFactoryFunctionRequest<any, any>) => (provider: string): IFactoryResponse<ConnectionTestResult, any> => {
  return axios({ method: EMethod.post, url: `${baseUrl}/${provider}/test` })
}

const runDiagnostic = (axios: IFactoryFunctionRequest<any, any>) => (provider: string, query: { includeArchived?: boolean } = {}, immediate = true): IFactoryResponse<DiagnosticReport, any> => {
  return axios({ method: EMethod.get, url: `${baseUrl}/${provider}/diagnostic`, query, immediate })
}

const searchProjects = (axios: IFactoryFunctionRequest<any, any>) => (provider: string, query: { search?: string; page?: number } = {}, immediate = true): IFactoryResponse<{ docs: IntegrationProjectOption[]; hasNextPage: boolean }, any> => {
  return axios({ method: EMethod.get, url: `${baseUrl}/${provider}/projects`, query, immediate })
}

const checkProject = (axios: IFactoryFunctionRequest<any, any>) => (provider: string, projectId: string): IFactoryResponse<ProjectCheckResult, any> => {
  return axios({ method: EMethod.get, url: `${baseUrl}/${provider}/projects/${projectId}/check` })
}

const syncProjects = (axios: IFactoryFunctionRequest<any, any>) => (provider: string, projectIds: string[]): IFactoryResponse<{ queued: number; jobIds: string[] }, any> => {
  return axios({ method: EMethod.post, url: `${baseUrl}/${provider}/sync`, data: { projectIds } })
}

export default (axios: AxiosInstance) => ({
  getTouchpoints: getTouchpoints(useAxios(axios)),
  updateSettings: updateSettings(useAxios(axios)),
  listIntegrations: listIntegrations(useAxios(axios)),
  getIntegration: getIntegration(useAxios(axios)),
  listJobs: listJobs(useAxios(axios)),
  getJob: getJob(useAxios(axios)),
  retryJob: retryJob(useAxios(axios)),
  dismissJob: dismissJob(useAxios(axios)),
  retryFailedJobs: retryFailedJobs(useAxios(axios)),
  testConnection: testConnection(useAxios(axios)),
  runDiagnostic: runDiagnostic(useAxios(axios)),
  searchProjects: searchProjects(useAxios(axios)),
  checkProject: checkProject(useAxios(axios)),
  syncProjects: syncProjects(useAxios(axios)),
})
